import { infiniteQueryOptions } from '@tanstack/react-query';

import { getCustomerIdExerciseExerciseIdRecords } from '~/shared/api/generated/endpoints/customer/customer';
import type { TrainWithMeModulesClientsDtosResponsesCustomerExerciseRecordsResponse as RecordsDto } from '~/shared/api/generated/model';
import { parseMeasurementFields } from '~/shared/measurement';

import type { LiftFields } from '../model/liftLines';
import type { LiftSetSource } from '../model/liftSessions';

/** Enough for years of one movement in one page; `nextBefore` carries anything older. */
const PAGE_SETS = 1000;

export function liftHistoryQueryKey(clientId: number, exerciseId: number) {
  return ['lift-history', clientId, exerciseId] as const;
}

export function liftHistoryQueryOptions(clientId: number, exerciseId: number) {
  return infiniteQueryOptions({
    queryKey: liftHistoryQueryKey(clientId, exerciseId),
    queryFn: ({ pageParam, signal }) =>
      getCustomerIdExerciseExerciseIdRecords(
        clientId,
        exerciseId,
        pageParam === null
          ? { recentSets: PAGE_SETS, includeMeasurementFields: true }
          : { recentSets: PAGE_SETS, includeMeasurementFields: true, before: pageParam },
        undefined,
        signal,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextBefore ?? undefined,
  });
}

export function toLiftSetSources(pages: readonly RecordsDto[]): readonly LiftSetSource[] {
  return pages.flatMap((page) =>
    (page.recentSets ?? []).map((set) => ({
      workoutExecutionId: set.workoutExecutionId ?? null,
      setId: set.setId ?? null,
      weightKg: set.weightKg ?? null,
      reps: set.reps ?? null,
      e1rmKg: set.e1rmKg ?? null,
      achievedAt: set.achievedAt ?? null,
      durationSeconds: set.durationSeconds ?? null,
      distanceMeters: set.distanceMeters ?? null,
      rounds: set.rounds ?? null,
      speedKmh: set.speedKmh ?? null,
    })),
  );
}

/** The movement's measured fields, from the first page; `null` from a backend that sends none. */
export function toLiftFieldsOf(pages: readonly RecordsDto[]): LiftFields {
  const wire = pages[0]?.measurementFields;
  return wire == null ? null : parseMeasurementFields(wire);
}
